"use client";

// Catches crashes in the root layout itself — the one place app/error.tsx
// can't reach. Because it replaces the entire document, it must render its
// own <html>/<body>. Mirrors the app/error.tsx look.
import { useEffect } from "react";
import { AlertTriangle } from "lucide-react";
import * as Sentry from "@sentry/nextjs";
import { captureClientException } from "@/lib/analytics";

export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    // Same route/digest context as app/error.tsx — see the note there. A root
    // layout crash is rarer but strictly harder to debug, so it needs this
    // more, not less.
    Sentry.withScope((scope) => {
      scope.setTag("error_boundary", "global");
      if (error.digest) scope.setTag("digest", error.digest);
      scope.setContext("route", {
        pathname: window.location.pathname,
        search: window.location.search,
      });
      Sentry.captureException(error);
    });

    // Same crash, second destination. Sentry keeps the symbolicated frames;
    // PostHog Error Tracking is where this sits next to the person who hit it,
    // the replay of them hitting it, and the product failure events from the
    // same session. The envelope (route, project, session, deploy) is stamped
    // by `before_send` — only what THIS boundary uniquely knows is passed here.
    captureClientException(error, {
      failurePhase: "render:global",
      extra: { error_boundary: "global", digest: error.digest ?? null },
    });
  }, [error]);

  return (
    <html>
      <body>
        <div className="flex min-h-screen items-center justify-center bg-white px-4">
          <div className="flex flex-col items-center gap-5 text-center">
            <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-red-50">
              <AlertTriangle className="h-6 w-6 text-red-500" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-zinc-900">
                Something went wrong
              </h2>
              <p className="mt-2 max-w-md text-sm text-zinc-500">
                {error.message || "An unexpected error occurred."}
              </p>
            </div>
            {/* Same chunk-load recovery as app/error.tsx: reset() can't
                re-download a failed chunk, a same-URL reload can. */}
            <button
              onClick={() => {
                if (error.name === "ChunkLoadError") {
                  window.location.reload();
                  return;
                }
                reset();
              }}
              className="rounded-full bg-zinc-900 px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-zinc-800"
            >
              Try again
            </button>
          </div>
        </div>
      </body>
    </html>
  );
}
